import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Paperclip, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  accountsCreatePurchase, accountsFileUrl, decidePurchaseRequest, decideRefundRequest, getRequestMeta,
  listAccountsPurchases, listAccountsRefunds, markPurchaseOrdered, markPurchasePaid, markRefundPaid, postPurchase,
  searchSalesForRefund, uploadAccountsFile, createRefundRequest, accountsCreateRefund,
} from "@/lib/api";
import { Empty, TH, TR, errMsg, money } from "@/components/accounts/shared";

const STATUS_TONE = {
  requested: "text-amber-300", approved: "text-blue-300", ordered: "text-blue-300", paid: "text-emerald-300", rejected: "text-zinc-500",
};
const today = () => new Date().toISOString().slice(0, 10);
const SELECT = "w-full bg-[#0a0a0a] border border-[#262626] rounded-md h-9 px-2 text-sm";

// The accounts side of refunds and purchases: every gym's requests, approve (by amount), pay, post.
export default function RequestQueue({ kind, country, gym, gyms }) {
  const isRefund = kind === "refund";
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("open");
  const [paying, setPaying] = useState(null);
  const [posting, setPosting] = useState(null);
  const [creating, setCreating] = useState(false);
  const [meta, setMeta] = useState(null);

  const load = useCallback(() => (isRefund ? listAccountsRefunds() : listAccountsPurchases())
    .then(setRows).catch((e) => toast.error(errMsg(e, "Could not load requests"))), [isRefund]);
  useEffect(() => { load(); getRequestMeta().then(setMeta).catch(() => {}); }, [load]);

  const decide = async (r, approve) => {
    const note = approve ? "" : window.prompt("Why is it rejected?");
    if (!approve && !note) return;
    try {
      await (isRefund ? decideRefundRequest : decidePurchaseRequest)(r.id, { approve, note });
      toast.success(approve ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(errMsg(e, "Could not save")); }
  };
  const ordered = async (r) => {
    try { await markPurchaseOrdered(r.id); load(); } catch (e) { toast.error(errMsg(e, "Could not save")); }
  };

  if (!rows) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const facGym = Object.fromEntries((gyms || []).map((g) => [g.id, g]));
  const visible = rows.filter((r) => (!gym || r.facility_id === gym) && (!country || facGym[r.facility_id]?.country === country)
    && (status === "all" || (status === "open" ? !["paid", "rejected"].includes(r.status) : r.status === status)));
  const count = (s) => rows.filter((r) => (s === "open" ? !["paid", "rejected"].includes(r.status) : r.status === s)).length;

  return (
    <div className="space-y-3" data-testid={`accounts-${kind}s`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {["open", "requested", "approved", ...(isRefund ? [] : ["ordered"]), "paid", "rejected", "all"].map((s) => (
            <button key={s} onClick={() => setStatus(s)}
              className={`text-xs px-3 py-1 rounded-full border ${status === s ? "border-blue-500/60 bg-blue-500/10 text-blue-200" : "border-[#262626] text-zinc-400"}`}>
              {s[0].toUpperCase() + s.slice(1)}{s !== "all" ? ` (${count(s)})` : ""}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={() => setCreating(true)} className="bg-blue-600 hover:bg-blue-500 h-8" data-testid={`new-${kind}`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> New {isRefund ? "refund" : "purchase"}</Button>
      </div>
      <div className="text-[11px] text-zinc-500">Approval by amount: facility head, then accounts, then master admin above the accounts limit. Limits per currency are in Settings.</div>
      <div className="card-surface overflow-x-auto">
        {visible.length === 0 ? <Empty>No {isRefund ? "refunds" : "purchases"} here.</Empty> : (
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className={TH}>
              <th className="px-3 py-2 font-medium">Requested</th><th className="px-3 py-2 font-medium">Gym</th>
              <th className="px-3 py-2 font-medium">{isRefund ? "Member · sale" : "Item"}</th>
              <th className="px-3 py-2 font-medium">Reason</th><th className="px-3 py-2 font-medium text-right">Amount</th>
              <th className="px-3 py-2 font-medium">Status</th><th className="px-3 py-2" />
            </tr></thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} className={TR}>
                  <td className="px-3 py-2 text-xs text-zinc-400">{String(r.requested_at).slice(0, 10)}<div className="text-[10px] text-zinc-500">{r.requested_by}</div></td>
                  <td className="px-3 py-2 text-zinc-400">{r.gym}</td>
                  <td className="px-3 py-2 text-zinc-200">
                    {isRefund ? <>{r.member}<div className="text-[10px] text-zinc-500">{r.programme} · sold {r.sale_date} · paid {money(r, r.paid_on_sale)}</div></>
                      : <>{r.item} {r.qty > 1 && <span className="text-zinc-500">× {r.qty}</span>}<div className="text-[10px] text-zinc-500">{r.vendor}</div></>}
                    {isRefund && <CoachIncentiveNote r={r} />}
                    {r.attachment_id && <a href={accountsFileUrl(r.attachment_id)} target="_blank" rel="noreferrer" className="text-[10px] text-blue-300 inline-flex items-center gap-0.5"><Paperclip className="h-3 w-3" /> attachment</a>}
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-400 max-w-[220px]">{r.reason}{r.decision_note && <div className="text-zinc-500 italic">{r.decision_note}</div>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r, r.paid_amount || r.amount)}
                    <div className="text-[10px] text-zinc-500">needs {r.approval_level_label}</div></td>
                  <td className={`px-3 py-2 text-xs ${STATUS_TONE[r.status]}`}>{r.status}
                    {r.status === "paid" && <div className="text-[10px] text-zinc-500">{r.paid_date} · {r.bank_ref}</div>}
                    {r.posted_to && <div className="text-[10px] text-zinc-500">posted to {r.posted_to}</div>}
                    {r.decided_by && r.status !== "paid" && <div className="text-[10px] text-zinc-500">by {r.decided_by}</div>}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.can_approve && <>
                      <Button size="sm" variant="ghost" onClick={() => decide(r, true)} className="h-7 text-emerald-300" data-testid="req-approve"><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
                      <Button size="sm" variant="ghost" onClick={() => decide(r, false)} className="h-7 text-rose-300"><X className="h-3.5 w-3.5 mr-1" />Reject</Button>
                    </>}
                    {!isRefund && r.status === "approved" && <Button size="sm" variant="ghost" onClick={() => ordered(r)} className="h-7">Ordered</Button>}
                    {r.can_pay && <Button size="sm" onClick={() => setPaying(r)} className="h-7 bg-blue-600 hover:bg-blue-500" data-testid="req-pay">Mark paid</Button>}
                    {!isRefund && r.status === "paid" && !r.posted_to && <Button size="sm" variant="outline" onClick={() => setPosting(r)} className="h-7 border-[#262626] bg-transparent">Post</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {paying && <PayDialog r={paying} isRefund={isRefund} onClose={() => setPaying(null)} onDone={() => { setPaying(null); load(); }} />}
      {posting && meta && <PostDialog r={posting} meta={meta} onClose={() => setPosting(null)} onDone={() => { setPosting(null); load(); }} />}
      {creating && (isRefund
        ? <NewRefund gyms={gyms} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} accounts />
        : <NewPurchase gyms={gyms} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} accounts />)}
    </div>
  );
}

function PayDialog({ r, isRefund, onClose, onDone }) {
  const [f, setF] = useState({ bank_ref: "", paid_date: today(), paid_amount: r.amount, invoice_no: "", attachment_id: null });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await (isRefund ? markRefundPaid : markPurchasePaid)(r.id, { ...f, paid_amount: Number(f.paid_amount) });
      toast.success(isRefund ? "Refund paid and recorded in FPA" : "Marked paid");
      onDone();
    } catch (e) { toast.error(errMsg(e, "Could not save")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle>{isRefund ? `Refund ${r.member}` : `Pay for ${r.item}`}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          {!isRefund && <label className="block space-y-1"><span className="text-xs text-zinc-400">Amount paid ({r.currency_code})</span>
            <Input type="number" value={f.paid_amount} onChange={(e) => setF({ ...f, paid_amount: e.target.value })} className="bg-[#0a0a0a] border-[#262626]" /></label>}
          <label className="block space-y-1"><span className="text-xs text-zinc-400">Bank reference / UTR</span>
            <Input value={f.bank_ref} onChange={(e) => setF({ ...f, bank_ref: e.target.value })} className="bg-[#0a0a0a] border-[#262626]" data-testid="pay-ref" /></label>
          <label className="block space-y-1"><span className="text-xs text-zinc-400">Date paid</span>
            <Input type="date" value={f.paid_date} onChange={(e) => setF({ ...f, paid_date: e.target.value })} className="bg-[#0a0a0a] border-[#262626]" /></label>
          {!isRefund && <>
            <label className="block space-y-1"><span className="text-xs text-zinc-400">Invoice number</span>
              <Input value={f.invoice_no} onChange={(e) => setF({ ...f, invoice_no: e.target.value })} className="bg-[#0a0a0a] border-[#262626]" /></label>
            <FilePick onUploaded={(id) => setF({ ...f, attachment_id: id })} label="Invoice (optional)" />
          </>}
          {isRefund && <p className="text-[11px] text-zinc-500">Saving records the refund in FPA dated the day paid (it comes off that month's revenue) and it shows as a debit in bank reconciliation.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || (isRefund && !f.bank_ref.trim())} className="bg-blue-600 hover:bg-blue-500" data-testid="pay-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PostDialog({ r, meta, onClose, onDone }) {
  const threshold = meta.capex_threshold?.[r.currency_code] || 0;
  const [to, setTo] = useState((r.paid_amount || 0) >= threshold && threshold ? "capex" : "expense");
  const [category, setCategory] = useState(to === "capex" ? "Strength & other gym equipment" : "Equipment");
  const [months, setMonths] = useState("");
  const save = async () => {
    try {
      await postPurchase(r.id, { to, category, months: months ? Number(months) : undefined });
      toast.success(`Posted to ${to === "capex" ? "Capex" : "Expenses"}`);
      onDone();
    } catch (e) { toast.error(errMsg(e, "Could not post")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle>Post {r.item} to the books</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500">{money(r, r.paid_amount)} paid {r.paid_date}. Capex threshold for {r.currency_code}: {money(r, threshold)} (depreciated monthly, like Expenses → Capital).</p>
        <div className="flex gap-2">
          {["expense", "capex"].map((t) => (
            <button key={t} onClick={() => { setTo(t); setCategory(t === "capex" ? "Strength & other gym equipment" : "Equipment"); }}
              className={`text-xs px-3 py-1 rounded-full border ${to === t ? "border-blue-500/60 bg-blue-500/10 text-blue-200" : "border-[#262626] text-zinc-400"}`}>{t === "capex" ? "Capex" : "Expense"}</button>
          ))}
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={SELECT}>
          {(to === "capex" ? meta.capex_categories.map((c) => c.name) : meta.expense_categories).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {to === "capex" && <Input type="number" placeholder="Months (blank = standard for the category)" value={months} onChange={(e) => setMonths(e.target.value)} className="bg-[#0a0a0a] border-[#262626]" />}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-blue-600 hover:bg-blue-500">Post</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FilePick({ onUploaded, label = "Attachment (optional)" }) {
  const [name, setName] = useState("");
  const pick = async (file) => {
    if (!file) return;
    try {
      const r = await uploadAccountsFile(file);
      setName(r.filename);
      onUploaded(r.id);
    } catch (e) { toast.error(errMsg(e, "Upload failed")); }
  };
  return (
    <label className="block space-y-1">
      <span className="text-xs text-zinc-400">{label}</span>
      <input type="file" accept="image/*,.pdf" onChange={(e) => pick(e.target.files?.[0])} className="block text-xs text-zinc-400" />
      {name && <span className="text-[11px] text-emerald-300">{name} uploaded</span>}
    </label>
  );
}

// On a refund of an FE conversion: what the Accountability Coach earned on this member (their FE
// Conversion incentive), and the extra on the rest of the coach's month when this member lifted them
// into a higher slab (Anish, 30 Oct 2026).
export function CoachIncentiveNote({ r }) {
  const ci = r.coach_incentive;
  if (!ci || !(ci.total > 0)) return null;
  const pct = (x) => `${Math.round((Number(x) || 0) * 1000) / 10}%`;
  return (
    <div className="mt-1 rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-[10px] text-amber-200 space-y-0.5" data-testid="refund-coach-incentive">
      <div>Coach FE Conversion incentive: {ci.coach} earned {money(r, ci.total, true)} on this member
        {ci.slab_extra > 0 && <> ({money(r, ci.on_member, true)} on the member + {money(r, ci.slab_extra, true)} from a higher slab)</>}</div>
      {ci.months.map((m) => (
        <div key={m.month + m.coach} className="text-amber-200/70">
          {m.month}: member {money(r, m.member_net, true)} at {pct(m.rate)}
          {m.slab_extra > 0 && <>; without them {m.coach}'s month was {pct(m.rate_without)}, payout {money(r, m.payout_without, true)} instead of {money(r, m.payout, true)}</>}
        </div>
      ))}
    </div>
  );
}

export function NewRefund({ gyms, onClose, onDone, accounts }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [sale, setSale] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [att, setAtt] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return undefined; }
    const t = setTimeout(() => searchSalesForRefund(q).then(setHits).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const save = async () => {
    try {
      await (accounts ? accountsCreateRefund : createRefundRequest)({ sale_id: sale.id, amount: Number(amount), reason, attachment_id: att });
      toast.success("Refund requested");
      onDone();
    } catch (e) { toast.error(errMsg(e, "Could not request the refund")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle>Request a refund</DialogTitle></DialogHeader>
        {!sale ? (
          <div className="space-y-2">
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Member name or phone" className="bg-[#0a0a0a] border-[#262626]" data-testid="refund-search" />
            <div className="max-h-60 overflow-y-auto">
              {hits.map((h) => (
                <button key={h.id} onClick={() => setSale(h)} className="w-full text-left px-2 py-1.5 hover:bg-[#1a1a1a] rounded text-sm">
                  {h.member_name} <span className="text-zinc-500 text-xs">{accounts ? `${h.gym} · ` : ""}{String(h.date).slice(0, 10)} · {h.programme} · paid {money(h, h.paid)}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="text-zinc-300">{sale.member_name} <span className="text-xs text-zinc-500">{sale.programme} · paid {money(sale, sale.paid)}</span>
              <button onClick={() => setSale(null)} className="ml-2 text-xs text-blue-300">change</button></div>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Amount (${sale.currency_code || ""})`} className="bg-[#0a0a0a] border-[#262626]" data-testid="refund-amount" />
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="bg-[#0a0a0a] border-[#262626]" data-testid="refund-reason" />
            <FilePick onUploaded={setAtt} />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!sale || !Number(amount) || !reason.trim()} className="bg-blue-600 hover:bg-blue-500" data-testid="refund-save">Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function NewPurchase({ gyms, onClose, onDone, accounts, create }) {
  const [f, setF] = useState({ facility_id: "", item: "", qty: 1, est_cost: "", vendor: "", reason: "", attachment_id: null });
  const save = async () => {
    try {
      const payload = { ...f, qty: Number(f.qty) || 1, est_cost: Number(f.est_cost) };
      await (accounts ? accountsCreatePurchase(payload) : create(payload));
      toast.success("Purchase requested");
      onDone();
    } catch (e) { toast.error(errMsg(e, "Could not request the purchase")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle>Request a purchase</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          {accounts && <select value={f.facility_id} onChange={(e) => setF({ ...f, facility_id: e.target.value })} className={SELECT}>
            <option value="">Gym…</option>{gyms.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>}
          <Input value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} placeholder="Item" className="bg-[#0a0a0a] border-[#262626]" data-testid="purchase-item" />
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} placeholder="Qty" className="bg-[#0a0a0a] border-[#262626]" />
            <Input type="number" value={f.est_cost} onChange={(e) => setF({ ...f, est_cost: e.target.value })} placeholder="Estimated total cost" className="bg-[#0a0a0a] border-[#262626]" data-testid="purchase-cost" />
          </div>
          <Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} placeholder="Vendor" className="bg-[#0a0a0a] border-[#262626]" />
          <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Why it's needed" className="bg-[#0a0a0a] border-[#262626]" />
          <FilePick onUploaded={(id) => setF({ ...f, attachment_id: id })} label="Quote (optional)" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!f.item.trim() || !Number(f.est_cost) || (accounts && !f.facility_id)} className="bg-blue-600 hover:bg-blue-500" data-testid="purchase-save">Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
